import React, { useMemo, useState } from "react";
import { fmtNumber } from "./shared";

export default function ByInstituteTab({
  institutes,
  allocations,
  lands,
  properties,
  units,
}) {
  const [activeId, setActiveId] = useState(institutes[0]?.id ?? "");
  const propById = useMemo(
    () => Object.fromEntries(properties.map((p) => [p.id, p])),
    [properties],
  );
  const unitById = useMemo(
    () => Object.fromEntries(units.map((u) => [u.id, u])),
    [units],
  );
  const landById = useMemo(
    () => Object.fromEntries(lands.map((l) => [l.id, l])),
    [lands],
  );

  const allocsForInst = allocations.filter(
    (a) => a.institute_id === activeId && a.is_active !== false,
  );
  const totalArea = allocsForInst.reduce(
    (s, a) => s + (Number(a.allocated_area) || 0),
    0,
  );

  const buildPath = (a) => {
    if (a.target_type === "property") {
      const p = propById[a.target_id];
      const land = p ? landById[p.land_id] : null;
      return land ? `${land.name} → ${a.target_name}` : a.target_name;
    }
    if (a.target_type === "unit") {
      const u = unitById[a.target_id];
      const p = u ? propById[u.property_id] : null;
      const land = p ? landById[p.land_id] : null;
      return [land?.name, p?.name, a.target_name].filter(Boolean).join(" → ");
    }
    return a.target_name;
  };

  return (
    <div
      data-testid="by-institute-tab"
      className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6"
    >
      <aside className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden">
        <div className="px-4 py-3 bg-[#f9f8f6] text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold border-b border-[#e5e0d8]">
          Institutes
        </div>
        <ul className="divide-y divide-[#e5e0d8]">
          {institutes.map((i) => (
            <li key={i.id}>
              <button
                onClick={() => setActiveId(i.id)}
                data-testid={`bi-inst-${i.id}`}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#f9f8f6]/60 ${
                  i.id === activeId
                    ? "bg-[#234333]/5 text-[#234333]"
                    : "text-[#1c1a17]"
                }`}
              >
                {i.name}
              </button>
            </li>
          ))}
          {institutes.length === 0 && (
            <li className="px-4 py-3 text-xs text-[#6b655b] italic">
              No institutes yet.
            </li>
          )}
        </ul>
      </aside>
      <section className="bg-white border border-[#e5e0d8] rounded-sm p-6 min-h-[300px]">
        {!activeId ? (
          <div className="text-sm text-[#6b655b] italic">
            Select an institute to view its assets.
          </div>
        ) : (
          <>
            <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">
                  Institute Assets
                </div>
                <h2 className="mt-1 font-serif text-3xl text-[#1c1a17]">
                  {institutes.find((i) => i.id === activeId)?.name || ""}
                </h2>
              </div>
              <div className="text-right">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b]">
                  Total allocated
                </div>
                <div className="font-serif text-2xl text-[#234333]">
                  {fmtNumber(totalArea)} sqft
                </div>
              </div>
            </div>
            {allocsForInst.length === 0 ? (
              <div className="text-sm text-[#6b655b] italic mt-4">
                No assets allocated to this institute yet.
              </div>
            ) : (
              <ul className="border-t border-[#e5e0d8]">
                {allocsForInst.map((a) => (
                  <li
                    key={a.id}
                    className="border-b border-[#e5e0d8] py-3 flex items-center justify-between gap-4"
                  >
                    <div>
                      <div className="text-sm text-[#1c1a17]">{buildPath(a)}</div>
                      <div className="text-[11px] text-[#6b655b] mt-0.5">
                        {a.allocation_type} · {a.target_type}
                        {a.start_date ? ` · since ${a.start_date}` : ""}
                        {a.end_date ? ` · until ${a.end_date}` : ""}
                      </div>
                    </div>
                    <div className="text-sm text-[#234333] font-mono whitespace-nowrap">
                      {fmtNumber(a.allocated_area)} sqft
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>
    </div>
  );
}
